if (!customElements.get('atomized-hero-carousel')) {
  const instances = new Set();
  let globalListenersBound = false;

  const handleGlobalVisibility = () => {
    instances.forEach((instance) => instance.handleDocumentVisibility());
  };

  const handleGlobalBlockSelect = (event) => {
    instances.forEach((instance) => instance.handleEditorBlockSelect(event));
  };

  const bindGlobalListeners = () => {
    if (globalListenersBound) return;
    document.addEventListener('visibilitychange', handleGlobalVisibility);
    document.addEventListener('shopify:block:select', handleGlobalBlockSelect);
    globalListenersBound = true;
  };

  const unbindGlobalListeners = () => {
    if (!globalListenersBound || instances.size) return;
    document.removeEventListener('visibilitychange', handleGlobalVisibility);
    document.removeEventListener('shopify:block:select', handleGlobalBlockSelect);
    globalListenersBound = false;
  };

  class AtomizedHero extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-dot]'));
      this.previousButton = this.querySelector('[data-previous]');
      this.nextButton = this.querySelector('[data-next]');
      this.pauseButton = this.querySelector('[data-pause]');
      this.status = this.querySelector('[data-status]');
      this.currentIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.delay = Number(this.dataset.delay) || 5000;
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.pauseReasons = new Set();
      this.manualPaused = false;
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.pointerStart = null;

      if (this.slides.length < 2) return;

      this.previousButton?.addEventListener('click', this.handlePrevious);
      this.nextButton?.addEventListener('click', this.handleNext);
      this.dots.forEach((dot) => dot.addEventListener('click', this.handleDotClick));
      this.pauseButton?.addEventListener('click', this.handlePauseClick);
      this.addEventListener('mouseenter', this.handleMouseEnter);
      this.addEventListener('mouseleave', this.handleMouseLeave);
      this.addEventListener('focusin', this.handleFocusIn);
      this.addEventListener('focusout', this.handleFocusOut);
      this.addEventListener('pointerdown', this.handlePointerDown);
      this.addEventListener('pointerup', this.handlePointerUp);
      this.addEventListener('pointercancel', this.handlePointerCancel);
      this.motionQuery.addEventListener('change', this.handleMotionChange);
      instances.add(this);
      bindGlobalListeners();
      this.schedule();
    }

    disconnectedCallback() {
      this.clearTimer();
      this.previousButton?.removeEventListener('click', this.handlePrevious);
      this.nextButton?.removeEventListener('click', this.handleNext);
      this.dots?.forEach((dot) => dot.removeEventListener('click', this.handleDotClick));
      this.pauseButton?.removeEventListener('click', this.handlePauseClick);
      this.removeEventListener('mouseenter', this.handleMouseEnter);
      this.removeEventListener('mouseleave', this.handleMouseLeave);
      this.removeEventListener('focusin', this.handleFocusIn);
      this.removeEventListener('focusout', this.handleFocusOut);
      this.removeEventListener('pointerdown', this.handlePointerDown);
      this.removeEventListener('pointerup', this.handlePointerUp);
      this.removeEventListener('pointercancel', this.handlePointerCancel);
      this.motionQuery?.removeEventListener('change', this.handleMotionChange);
      instances.delete(this);
      unbindGlobalListeners();
      this.initialized = false;
    }

    handlePrevious = () => this.manualMove(this.currentIndex - 1);
    handleNext = () => this.manualMove(this.currentIndex + 1);
    handleDotClick = (event) => this.manualMove(Number(event.currentTarget.dataset.dot));
    handlePauseClick = () => this.toggleManualPause();
    handleMouseEnter = () => this.addPauseReason('hover');
    handleMouseLeave = () => this.removePauseReason('hover');
    handleFocusIn = () => this.addPauseReason('focus');
    handleFocusOut = (event) => {
      if (!this.contains(event.relatedTarget)) this.removePauseReason('focus');
    };
    handlePointerDown = (event) => this.onPointerDown(event);
    handlePointerUp = (event) => this.onPointerUp(event);
    handlePointerCancel = () => {
      this.pointerStart = null;
    };
    handleDocumentVisibility() {
      if (document.hidden) this.addPauseReason('visibility');
      else this.removePauseReason('visibility');
    }

    handleMotionChange = () => {
      this.clearTimer();
      this.schedule();
    };

    handleEditorBlockSelect(event) {
      const slide = this.querySelector(`[data-block-id="${event.detail.blockId}"]`);
      if (!slide) return;
      this.addPauseReason('editor');
      this.goTo(Number(slide.dataset.index), false);
    }

    addPauseReason(reason) {
      this.pauseReasons.add(reason);
      this.clearTimer();
    }

    removePauseReason(reason) {
      this.pauseReasons.delete(reason);
      this.schedule();
    }

    toggleManualPause() {
      this.manualPaused = !this.manualPaused;
      this.pauseButton.classList.toggle('is-paused', this.manualPaused);
      this.pauseButton.setAttribute('aria-pressed', String(this.manualPaused));
      this.pauseButton.setAttribute(
        'aria-label',
        this.manualPaused ? this.pauseButton.dataset.playLabel : this.pauseButton.dataset.pauseLabel,
      );
      this.clearTimer();
      this.schedule();
    }

    manualMove(index) {
      this.goTo(index, true);
      this.clearTimer();
      this.schedule();
    }

    goTo(index, announce) {
      const normalized = (index + this.slides.length) % this.slides.length;
      if (normalized === this.currentIndex) return;

      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === normalized;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.toggleAttribute('inert', !active);
      });
      this.dots.forEach((dot, dotIndex) => {
        const active = dotIndex === normalized;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', String(active));
      });
      this.currentIndex = normalized;
      if (announce && this.status) this.status.textContent = `${normalized + 1} / ${this.slides.length}`;
    }

    schedule() {
      this.clearTimer();
      if (
        !this.autoplayEnabled ||
        this.manualPaused ||
        this.pauseReasons.size ||
        this.motionQuery.matches ||
        document.hidden
      ) {
        return;
      }
      this.timer = window.setTimeout(() => {
        this.goTo(this.currentIndex + 1, false);
        this.schedule();
      }, this.delay);
    }

    clearTimer() {
      if (this.timer) window.clearTimeout(this.timer);
      this.timer = null;
    }

    onPointerDown(event) {
      if (event.pointerType === 'mouse' || event.target.closest('a, button')) return;
      this.pointerStart = { x: event.clientX, y: event.clientY, id: event.pointerId };
    }

    onPointerUp(event) {
      if (!this.pointerStart || this.pointerStart.id !== event.pointerId) return;
      const deltaX = event.clientX - this.pointerStart.x;
      const deltaY = event.clientY - this.pointerStart.y;
      this.pointerStart = null;
      if (Math.abs(deltaX) < 42 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
      this.manualMove(deltaX < 0 ? this.currentIndex + 1 : this.currentIndex - 1);
    }
  }

  customElements.define('atomized-hero-carousel', AtomizedHero);
}
